import { Box, Button, useTheme, Autocomplete, TextField, Typography, Chip } from "@mui/material";
import React, { useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import ApiResources from "src/providers/ApiResources";
import MultipartUpload from "src/providers/MultipartUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AddLinkIcon from "@mui/icons-material/AddLink";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useNotify } from 'react-admin';

export const FileUpload = (props: FileUpload) => {
  const { fileTypes, path, directories, selectedDirectory, handleRefresh, handleSingleInsert, setLoading, lockPath = false } = props;
  const notify = useNotify();
  const [uploads, setUploads] = useState([]);
  const [directoryValue, setDirectoryValue] = useState(selectedDirectory ? [selectedDirectory] : []);
  const [text, setText] = useState("Upload or drop files here");

  const handleUpload = async () => {
    setText("Uploading...");
    setLoading(true);
    for (let i = 0; i < uploads.length; i++) {
      await uploadFile(i);
    }
    setText("Upload completed.");
    setLoading(false);
    handleRefresh(directoryValue.join(''));
  };

  async function uploadFile(i: number) {
    if (uploads[i].uploaded) return;

    const data = new FormData();
    data.append("files[]", uploads[i].file);

    if (directoryValue.length > 0) {
      data.append("path", directoryValue.join(''));  
    }

    await MultipartUpload(
      ApiResources.postImage(path),
      data
    ).then(response => {
      return response.json();
    }).then(response => {
      if (Array.isArray(response)) {
        let newUploads = [...uploads];
        newUploads[i].uploaded = true;
        newUploads[i].path = response[0]['file'];
        setUploads(newUploads);
      } else {
        if (response?.code && response?.code !== 200 && response?.errors?.files) {
          notify(response?.errors?.files.join(', '), { type: 'warning' });
        } else {
          notify(response.message, { type: 'warning' });
        }
      }
    }).catch(error => {
      notify(error.message, { type: 'warning' });
    });
  }

  const handleChange = (fileList: FileList) => {
    let newUploads = [...uploads];
    for (let i = 0; i < fileList.length; i++) {
      newUploads.push({
        name: fileList.item(i).name,
        uploaded: false,
        file: fileList.item(i),
      });
    }
    setUploads(newUploads);
  };

  const removeFromUploads = (name: string) => {
    let newUploads = [...uploads].filter((upload) => upload.name !== name);
    setUploads(newUploads);
  };

  const theme = useTheme();

  const boxStyle = {
    display: "flex",
    justifyContent: "space-between",
    color: theme.palette.grey["600"],
    padding: "20px",
    backgroundColor: theme.palette.grey["100"],
    borderRadius: 1,
    width: "100%",
    boxSizing: "border-box"
  };

  const changeDirectoryValueChange = (value: string[]) => {
    let valueWithTrails = value.map((path) => {
      return path.replace(/\/?$/, '/');
    });

    setDirectoryValue(valueWithTrails)
  }

  return (
    <React.Fragment>
      {!lockPath &&
      <Box display="flex" alignItems="center" gap="7px" sx={{ paddingBottom: "15px"}}>
        <Typography component="span" sx={{ fontSize: "12px", fontStyle: "italic", fontWeight: 600}}>Select Directory</Typography>
        <Autocomplete
          multiple
          size="small"
          options={directories}
          freeSolo
          value={directoryValue}
          onChange={(event, value) => changeDirectoryValueChange(value)}
          getOptionDisabled={(option) =>
            directoryValue.length > 0
          }
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                variant="outlined" 
                label={option} 
                {...getTagProps({ index })} 
                disabled={index+1 < value.length}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Directory Path"
            />
          )}
          sx={{flex: "1"}}
        />
      </Box>
      }
      <FileUploader
        types={fileTypes}
        multiple={true}
        handleChange={(fileList: FileList) => handleChange(fileList)}
        name="files"
      >
        <Box sx={boxStyle}>
          <Box display="flex" alignItems="center" gap="7px">
            <Box>{text}</Box>
            <UploadFileIcon sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Box sx={typesStyle}>{fileTypes.join(", ")}</Box>
        </Box>
      </FileUploader>
      <Box>
        {uploads.map((upload, index) => {
          return (
            <Box key={index} sx={uploadBoxStyle}>
              <Box display="flex" alignItems="center">
                {upload.uploaded ?
                  <AddLinkIcon 
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSingleInsert(upload.path)}
                  />
                :
                  <CloseIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => removeFromUploads(upload.name)}
                  />
                }
                <Box sx={{fontSize: '14px', marginLeft: '10px'}}>{upload.name}</Box>
              </Box>

              {upload.uploaded ? <CheckCircleIcon /> : null}
            </Box>
          );
        })}
      </Box>
      <Box display="flex" justifyContent="end">
        {uploads.length > 0 ? (
          <Button
            variant="outlined"
            sx={{ marginTop: "0.7em" }}
            onClick={() => handleUpload()}
          >
            Upload All
          </Button>
        ) : null}
      </Box>
    </React.Fragment>
  );
};

const uploadBoxStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.3em",
  backgroundColor: "#00509f",
  color: "white",
  fontWeight: "bold",
  borderRadius: "5px",
  marginY: "0.3em",
};

const typesStyle = {
  width: "40%",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
};

export type FileUpload = {
  fileTypes: string[];
  directories: string[];
  path: string;
  selectedDirectory?: string;
  handleRefresh: any;
  handleSingleInsert: any;
  setLoading: any;
  lockPath?: boolean;
};
